import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import "./donut-chart.scss";
import Typography from "../../typography/typography";
import { useAuth } from '@erpsquad/common/runtime';

interface DataPoint {
  label: string;
  value: number;

}

interface DonutChartProps {
  data: DataPoint[];
  width?: number;
  height?: number;
  legend?: boolean;
  direction?: "vertical" | "horizontal";
  total?: string;
  donutThickerValue?: number
  CustomLegends?: React.FC<{ data: DataPoint[] }>;
  title: string
}

const formatNumber = (num: number, defaultCurrencySymbol: string = ''): string => {
  if(!num || Number.isNaN(num)) num = 0;

  if (num >= 1_000_000) {
    return `${defaultCurrencySymbol} ${(num / 1_000_000).toFixed(1)} M`;
  } else if (num >= 100_000) {
    return `${defaultCurrencySymbol} ${(num / 100_000).toFixed(1)} L`; // L for Lakhs
  } else if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)} K`;
  } else {
    return `${defaultCurrencySymbol} ${num.toFixed(2)}`;
  }
};
const getSingleNumberFromString = (str) => {
  if (typeof str === "number") {
    return str;
  }
  const match = str?.match(/\d.+/) || null;
  return match ? Number(match[0]) : 0;
};

const DonutChart: React.FC<DonutChartProps> = ({
  data,
  height = 140,
  width = 140,
  legend = true,
  direction = "vertical",
  total = "300",
  donutThickerValue,
  CustomLegends,
  title = ''
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const { user } = useAuth();
  const defaultCurrencySymbol = user?.currency_data?.symbol || '';
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);

    svg.selectAll("*").remove();

    const margin = 6;

    const outerRadius = Math.min(width, height) / 2 - margin;
    let innerRadius = Math.min(width, height) / 2 - margin * 2;
    if (donutThickerValue) {
      innerRadius = outerRadius - donutThickerValue;
    }




    const g = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);
    const pie = d3
      .pie()
      .value((d: DataPoint) => getSingleNumberFromString(d.value)).padAngle(0);

    const path = d3.arc().outerRadius(outerRadius).innerRadius(innerRadius)

    const pies = g
      .selectAll(".arc")
      .data(pie(data))
      .enter()
      .append("g")
      .attr("class", "arc");


    pies
      .append("path")
      .attr("d", path)
      .attr("fill", (d: DataPoint) => d?.data?.color).attr("stroke", "none");
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("font-size", "18px")
      .attr("font-weight", "600")
      .attr("alignment-baseline", `central`)
      .html(total);
    g.append("text")
      .attr("text-anchor", "middle")
      .attr('maring-top', "20px")
      .attr("font-size", "13px")
      .attr("font-weight", "600")
      .attr("dy", "2em")
      .attr("color", "#7B7C7F")
      .html(title);

  }, [data, height, total, width, defaultCurrencySymbol, donutThickerValue, CustomLegends, title]);

  if (!legend) {
    return <svg ref={svgRef} width={width} height={height}></svg>;
  }

  return (
    <>
      <div className={`donut-chart-container ${direction}`}>
        <div>
          <svg ref={svgRef} width={width} height={height}></svg>
        </div>


        {CustomLegends ? (
          <CustomLegends data={data} /> // Render the custom component
        ) : (
          <div className='donut-legend'>
            <div className={`donut-chart-legend ${direction}`}>
              {data.map((d: DataPoint) => (
                <div
                  className="legend"
                  key={d.label}
                  style={{ borderLeft: `0.188rem solid ${d.color}` }}
                >
                  <div className="legend-title" style={{ color: `${d.labelColor}` }}>{d.label}</div>
                  <div className="legend-value">
                    <Typography
                      type="s5"
                      weight="medium"
                      color={d.valueColor}
                      style={{
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {formatNumber(d.value, defaultCurrencySymbol)}
                    </Typography>
                  </div>
                </div>
              ))
              }
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export { DonutChart };
export default DonutChart;
